import { FC, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";

import { useData } from "data/data-provider";
import DatePickerCustomHeaderOneMonth from "components/DatePickerCustomHeaderOneMonth";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";

export interface HoursViewProps {
  // Mirror the props the Dates tab calendar uses so the Hours tab calendar
  // renders identically (availability underlines, disabled unavailable
  // dates, month-change fetching).
  isDateSelectable: (date: Date) => boolean;
  renderDayContents: (day: number, date?: Date) => React.ReactNode;
  handleMonthChange: (date: Date) => void;
  calendarViewDate: Date | null;
  monthsToShow: number;
}

// 1NOW-977: hourly booking mode. Quick Select durations (hours).
const QUICK_SELECT = [2, 4, 6, 8, 12, 24];

// 1-hour increments only (per spec) — coarser than the Dates+Times combo's
// 30-min slots, matching Template's HoursView.
const timeOptions = Array.from({ length: 24 }, (_, h) =>
  `${h.toString().padStart(2, "0")}:00`
);

const HoursView: FC<HoursViewProps> = ({
  isDateSelectable,
  renderDayContents,
  handleMonthChange,
  calendarViewDate,
  monthsToShow,
}) => {
  const {
    startDate,
    endDate,
    startTime,
    endTime,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    setIsHourlyRental,
    pickupLocation,
    ourCompany,
  } = useData();

  const use24Hour = ourCompany?.default_time_format === "24_hour";

  // Seed defaults: today 10:00 -> today 16:00 (6h block) on first open.
  useEffect(() => {
    if (!startDate) {
      setStartDate(new Date());
      setStartTime("10:00");
    }
    if (!endDate) {
      setEndDate(new Date());
      setEndTime("16:00");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startMoment = useMemo(
    () =>
      startDate && startTime
        ? moment(
            `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
            "YYYY-MM-DD HH:mm"
          )
        : null,
    [startDate, startTime]
  );
  const endMoment = useMemo(
    () =>
      endDate && endTime
        ? moment(
            `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
            "YYYY-MM-DD HH:mm"
          )
        : null,
    [endDate, endTime]
  );

  // Current pickup -> drop-off gap in whole hours, used to mark the active
  // Quick Select chip.
  const gapHours = useMemo(() => {
    if (!startMoment || !endMoment) return null;
    const diff = endMoment.diff(startMoment, "hours", true);
    return diff > 0 ? diff : null;
  }, [startMoment, endMoment]);

  // Quick Select: drop-off = pickup + N hours.
  const applyQuickSelect = (hours: number) => {
    if (!startMoment) return;
    setIsHourlyRental(true);
    const newEnd = startMoment.clone().add(hours, "hours");
    setEndDate(newEnd.toDate());
    setEndTime(newEnd.format("HH:mm"));
  };

  // Block times outside the pickup location's operational hours.
  const opStart = pickupLocation?.operational_start_time ?? null;
  const opEnd = pickupLocation?.operational_end_time ?? null;
  const isTimeOperational = (time: string): boolean => {
    if (!opStart || !opEnd) return true;
    const t = moment(time, "HH:mm");
    return (
      !t.isBefore(moment(opStart, ["HH:mm:ss", "HH:mm"])) &&
      !t.isAfter(moment(opEnd, ["HH:mm:ss", "HH:mm"]))
    );
  };

  const formatTime = (time: string) => {
    const m = moment(time, "HH:mm");
    return use24Hour ? m.format("HH:mm") : m.format("h:mm A");
  };

  // Range calendar handler — react-datepicker's selectsRange fires
  // [start, null] on the first click of a fresh range and [start, end] on
  // the second.
  const onRangeChange = (dates: [Date | null, Date | null]) => {
    setIsHourlyRental(true);
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div>
      {/* Quick Select chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {QUICK_SELECT.map((hours) => {
          const isActive = gapHours === hours;
          return (
            <button
              key={hours}
              type="button"
              disabled={!startMoment}
              onClick={(e) => {
                e.preventDefault();
                applyQuickSelect(hours);
              }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive
                  ? "bg-primary-200 text-white"
                  : "border border-neutral-200 text-text-color hover:bg-neutral-50"
              }`}
            >
              {hours} {hours === 1 ? "hr" : "hrs"}
            </button>
          );
        })}
      </div>

      {/* Pick-up / drop-off time */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <span className="block text-xs font-medium text-text-color mb-1">
            Pick-up time
          </span>
          <div className="max-h-32 overflow-y-auto border border-neutral-200 rounded-md">
            {timeOptions.map((time) => {
              const operational = isTimeOperational(time);
              return (
                <div
                  key={time}
                  onClick={() => {
                    if (!operational) return;
                    setIsHourlyRental(true);
                    setStartTime(time);
                  }}
                  className={`px-3 py-1.5 text-sm cursor-pointer ${
                    startTime === time ? "bg-primary-200 text-white" : ""
                  } ${
                    !operational
                      ? "opacity-40 line-through cursor-not-allowed"
                      : "hover:bg-neutral-50"
                  }`}
                >
                  {formatTime(time)}
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <span className="block text-xs font-medium text-text-color mb-1">
            Drop-off time
          </span>
          <div className="max-h-32 overflow-y-auto border border-neutral-200 rounded-md">
            {timeOptions.map((time) => {
              const operational = isTimeOperational(time);
              return (
                <div
                  key={time}
                  onClick={() => {
                    if (!operational) return;
                    setIsHourlyRental(true);
                    setEndTime(time);
                  }}
                  className={`px-3 py-1.5 text-sm cursor-pointer ${
                    endTime === time ? "bg-primary-200 text-white" : ""
                  } ${
                    !operational
                      ? "opacity-40 line-through cursor-not-allowed"
                      : "hover:bg-neutral-50"
                  }`}
                >
                  {formatTime(time)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {opStart && opEnd && (
        <p className="text-xs text-text-color opacity-60 mb-4">
          Available {formatTime(moment(opStart, ["HH:mm:ss", "HH:mm"]).format("HH:mm"))} –{" "}
          {formatTime(moment(opEnd, ["HH:mm:ss", "HH:mm"]).format("HH:mm"))}
        </p>
      )}

      {/* Inline range calendar — same config as the Dates tab */}
      <DatePicker
        selected={startDate}
        onChange={(dates) => onRangeChange(dates as [Date | null, Date | null])}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        monthsShown={monthsToShow}
        showPopperArrow={false}
        inline
        minDate={new Date()}
        openToDate={calendarViewDate || startDate || new Date()}
        filterDate={isDateSelectable}
        renderCustomHeader={(p) => (
          <>
            <div className="block sm:hidden text-text-color">
              <DatePickerCustomHeaderOneMonth {...p} />
            </div>
            <div className="hidden sm:block text-text-color">
              <DatePickerCustomHeaderTwoMonth {...p} />
            </div>
          </>
        )}
        renderDayContents={renderDayContents}
        onMonthChange={handleMonthChange}
      />
    </div>
  );
};

export default HoursView;
